/**
 * Container component for the ranks table.
 */

import { useEffect, useRef, useState } from 'react';
import {
  sortDataByColumn,
  getSelectedRegions,
  getRegionOptions
} from '../../utils/tables';
import type { TableColumn } from '../../types/TableColumn';
import type { Rank } from '../../types/Rank';
import { getRanks } from '../../utils/api';
import { useData } from '../../hooks/useData';
import TableLoader from './TableLoader';
import TableContainer from './TableContainer';
import TableBody from './TableBody';
import TableHead from './TableHead';
import TableHeader from './TableHeader';
import Table from './Table';
import RefreshButton from '../buttons/RefreshButton';
import { getMissingLocationIds } from '../../utils/locations';
import DropdownRegionMultiple from '../dropdowns/DropDownRegionMuli';
import type { RegionsStats } from '../../types/RegionsStats';
import { app } from '../../firebase';
import {
  collection,
  doc,
  onSnapshot,
  getFirestore,
  type Unsubscribe,
} from 'firebase/firestore';

const tableTitle = 'Location Ranks';

export default function RanksTable(): JSX.Element {
  const {
    selectedFranchise,
    selectedDates,
    selectedLocations,
    toggled,
    userLocations,
    selectedRegionIds,
    handleSelectedRegionIds,
  } = useData();

  const [columns, setColumns] = useState<TableColumn[]>([
    { label: 'Rank', key: 'rank', type: 'number' },
    {
      label: 'Location',
      key: 'locationName',
      type: 'link',
      href: '/cameras/?lid=locationId',
    },
    { label: 'Average', key: 'averageServiceTime', type: 'seconds' },
    { label: 'Customer Count', key: 'customerCount', type: 'number' },
    {
      label: 'Difference From Best Day',
      key: 'differenceFromBestDay',
      type: 'secondsDifference',
    },
    {
      label: 'WOW Change',
      key: 'weekOverWeekChangeInTime',
      type: 'secondsDifference',
    },
    { label: 'Rank Change', key: 'rankChange', type: 'numberDifference' },
  ]); // Use Column[] as the type
  const [data, setData] = useState<Rank[]>([]);
  const [displayData, setDisplayData] = useState<Rank[]>([]);
  const [sortColumn, setSortColumn] = useState(columns[0]);
  const [sortDirection, setSortDirection] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRegions, setSelectedRegions] = useState<any[]>([]);
  const [regions, setRegions] = useState<any[]>([]);
  const dataRef = useRef<Rank[]>([]); // Holds all rows data
  const [, setDummyState] = useState(0); // Used to trigger selective updates
  // Use another ref to avoid infinite rerendering loop
  const isUpdatingRef = useRef(false);
  const [rankTime, setRankTime] = useState<Record<string, number>>({});
  const [initialLoad, setInitialLoad] = useState<Boolean>(true);

  // for canceling requests and avoiding stale data
  const [abortController, setAbortController] =
    useState<AbortController | null>(null);

  function getUniqueRegions(rawData: RegionsStats[]): Record<string, string> {
    if (rawData.length === 0) return {};
    const uniqueRegions: Record<string, string> = {};
    // Get all possible regions.
    for (const regionData of rawData) {
      for (const r of regionData.regions) {
        uniqueRegions[r.key] = r.displayName;
      }
    }
    return uniqueRegions;
  }

  function handleSelectedRegionsChange(id: string): void {
    let newSelectedRegions;
    const regionInSelected = selectedRegions.find((region) => region.id === id);
    if (regionInSelected !== undefined) {
      newSelectedRegions = selectedRegions.filter((region) => region.id !== id);
      handleSelectedRegionIds(newSelectedRegions, setSelectedRegions);
    } else {
      const region = regions.find((region) => region.id === id);
      newSelectedRegions = [...selectedRegions, region];
      handleSelectedRegionIds([...selectedRegions, region], setSelectedRegions);
    }

    const regionKeys = newSelectedRegions.map((r) => r.id);
    const newData = data;

    newData.forEach((location, index) => {
      const values: number[] = [];

      location.regionsAgg.regions.forEach((r) => {
        if (regionKeys.includes(r.key)) {
          values.push(r.timeSpent);
        } else {
          values.push(0);
        }
      });

      // Ensuring rank average time is used when ALL regions are selected.
      if (newSelectedRegions.length == regions.length) {
        newData[index].averageServiceTime = rankTime[location.locationId]
      }
      else {
        newData[index].averageServiceTime = values.reduce((a, b) => a + b);
      }

    });

    const col = columns.find((c) => c.key === 'averageServiceTime');
    if (col !== undefined) {
      backGroundSort(col, newData);
    }
  }

  async function fetchData(clearDefaultRegions: boolean = false): Promise<void> {
    if (selectedFranchise === null) {
      return;
    }
    setIsLoading(true);

    const oneWeekAgo = new Date(selectedDates[0]);
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    const regionEndDate = new Date(selectedDates[0]);
    regionEndDate.setDate(regionEndDate.getDate() + 1);

    if (abortController) {
      abortController.abort();
    }

    const newAbortController = new AbortController();
    setAbortController(newAbortController);

    const rankDataPayload = await getRanks(
      selectedFranchise.id,
      oneWeekAgo,
      selectedDates[0],
      userLocations
        .filter((location) => selectedLocations.includes(location))
        .map((location) => location.id),
      toggled,
      { signal: newAbortController.signal }
    );

    const rankData = rankDataPayload.rankData;
    const regionData = rankDataPayload.regionForRankData;
    const modifiedData = addBlankLocations(rankData);

    modifiedData.forEach((location, index) => {
      setRankTime(prevRankTime => ({
        ...prevRankTime,
        [modifiedData[index].locationId]: modifiedData[index].averageServiceTime
      }));
      const matchingRegionEntries = regionData.filter(
        (regLocation) => regLocation.locationId === location.locationId
      );

      if (matchingRegionEntries.length > 0) {
        const matchingRegionEntry: RegionsStats = {
          locationId: location.locationId,
          locationName: location.locationName,
          regions: matchingRegionEntries.map(entry => ({
            key: entry.region,
            displayName: entry.regionName,
            timeSpent: entry.averageServiceTime
          }))
        };
        modifiedData[index].regionsAgg = matchingRegionEntry;
      } else {
        modifiedData[index].regionsAgg = {
          locationId: location.locationId,
          locationName: location.locationName,
          regions: []
        };
        modifiedData[index].averageServiceTime = 0;
      }
    });

    const uniqueRegions = getUniqueRegions(modifiedData.map((d) => d.regionsAgg));
    setRegions(getRegionOptions(uniqueRegions));
    handleSelectedRegionIds(
      getSelectedRegions(clearDefaultRegions ? [] : selectedRegionIds, uniqueRegions),
      setSelectedRegions
    );

    const col = columns.find((c) => c.key === 'averageServiceTime');
    if (col !== undefined) {
      backGroundSort(col, modifiedData);
    }
    setIsLoading(false);
  }

  function setColumnsFunction(): void {
    const shouldIncludeCurrentCount =
      toggled === 'LOBBY' &&
      new Date().toDateString() === new Date(selectedDates[0]).toDateString();
    const shouldIncludeOpenAndClose =
      typeof window !== 'undefined' && window.location.hostname.includes('avs');
    const baseColumns = [
      { label: 'Rank', key: 'rank', type: 'number' },
      {
        label: 'Location',
        key: 'locationName',
        type: 'link',
        href: '/cameras/?lid=locationId',
      },
      { label: 'Average', key: 'averageServiceTime', type: 'seconds' },
      { label: 'Customer Count', key: 'customerCount', type: 'number' },
      ...(shouldIncludeCurrentCount
        ? [{ label: 'Current Count', key: 'currentCount', type: 'number' }]
        : []),
      {
        label: 'Difference From Best Day',
        key: 'differenceFromBestDay',
        type: 'secondsDifference',
      },
      {
        label: 'WOW Change',
        key: 'weekOverWeekChangeInTime',
        type: 'secondsDifference',
      },
      { label: 'Rank Change', key: 'rankChange', type: 'numberDifference' },
      ...(shouldIncludeOpenAndClose
        ? [{ label: 'Open Time', key: 'openTime', type: 'timeOpen' },
        { label: 'Close Time', key: 'closeTime', type: 'timeClose' }]
        : []),
    ];

    // if (shouldIncludeCurrentCount) {
    //   baseColumns.push({ label: 'Current Count', key: 'currentCount', type: 'number' });
    // }

    setColumns(baseColumns);
  }

  // Fetch the data on load.
  useEffect(() => {
    setColumnsFunction();
    fetchData(true).catch((err) => {
      console.error(err);
    }).finally(() => {
      setInitialLoad(false);
    });
  }, []);

  // Specific for  toggle change.
  useEffect(() => {
    if (initialLoad) {
      return;
    }
    setColumnsFunction();
    fetchData(true).catch((err) => {
      console.error(err);
    });
  }, [toggled, selectedFranchise,]);

  // Fetch the data when the dates, franchise
  useEffect(() => {
    if (initialLoad) {
      return;
    }
    setColumnsFunction();
    fetchData(false).catch((err) => {
      console.error(err);
    });
  }, [selectedDates[0], selectedLocations]);

  // Set the data to display based on the selected locations.
  useEffect(() => {
    if (initialLoad) {
      return;
    }
    const unsubscribes: Unsubscribe[] = [];
    const firestore = getFirestore(app);

    dataRef.current = [...data];

    selectedLocations.forEach((location) => {
      const locationDoc = doc(collection(firestore, 'location'), location.id);
      const unsub = onSnapshot(locationDoc, (docSnapshot) => {
        const fbData = docSnapshot.data();
        if (fbData?.current_volume != null) {
          // Find the relevant location in dataRef and update its value
          const index = dataRef.current.findIndex(
            (row) => row.locationId === location.id
          );
          if (index !== -1) {
            dataRef.current[index].currentCount = fbData.current_volume;

            // Trigger a minor update without a full re-render
            if (!isUpdatingRef.current) {
              isUpdatingRef.current = true;
              setDummyState((prev) => prev + 1); // Minor state update
              isUpdatingRef.current = false;
            }
          }
        }
      });
      unsubscribes.push(unsub);
    });

    // setDisplayData(data.filter((row) => selected.includes(row.locationName)));

    return () => {
      unsubscribes.forEach((unsubscribe) => {
        unsubscribe();
      });
    };
  }, [selectedLocations, data]);

  // UseEffect to set displayData initially based on dataRef, without rerendering every update
  useEffect(() => {
    if (initialLoad) {
      return;
    }
    setDisplayData(dataRef.current);
  }, [data, selectedLocations]);

  // Sort a column.
  function handleSort(column: TableColumn): void {
    const newData = sortDataByColumn(dataRef.current, column, sortDirection);
    dataRef.current = [...newData]; // Populate ref initially
    setData(newData);
    setSortColumn(column);
    setSortDirection(sortDirection * -1);
  }

  // Add blank locations to the display data.
  function addBlankLocations(data: Rank[]): Rank[] {
    const missingLocationIds = getMissingLocationIds(
      selectedLocations.map((location) => location.id),
      data.map((row) => row.locationId)
    );

    for (const id of missingLocationIds) {
      const location = selectedLocations.find((location) => location.id === id);
      const regions =
        toggled === 'lobby'
          ? (location?.lobbyConfiguration?.regions as any[])
          : (location?.dtConfiguration?.regions as any[]);
      if (location !== undefined && regions !== undefined) {
        for (const region of regions) {
          region.timeSpent = 0;
        }
        data.push({
          rank: 0,
          locationName: location.displayName,
          averageServiceTime: 0,
          customerCount: 0,
          differenceFromBestDay: 0,
          weekOverWeekChangeInTime: 0,
          rankChange: 0,
          currentCount: 0,
          locationId: location.id,
          regionsAgg: {
            locationId: location.id,
            locationName: location.displayName,
            regions,
          },
        } as Rank);
      }
    }
    return data;
  }

  function backGroundSort(column: TableColumn, oldData: Rank[]): void {
    const newData = sortDataByColumn(oldData, column, 1);
    newData.forEach((_, index) => (newData[index].rank = index + 1));
    setData(newData);
    setSortColumn(column);
    setSortDirection(1);
  }

  if (selectedFranchise === null || isLoading) {
    return (
      <TableLoader
        columns={columns.map((column) => column.label)}
        title={tableTitle}
        handleRefresh={() => {
          fetchData().catch((err) => {
            console.error(err);
          });
        }}
      />
    );
  }

  return (
    <TableContainer dataCy="ranks-table">
      <TableHeader title={tableTitle}>
        <DropdownRegionMultiple
          regionStats={regions}
          selectedRegionStats={selectedRegions}
          handlSelectedRegionStatsChange={handleSelectedRegionsChange}
        />
        <RefreshButton
          handleRefresh={() => {
            fetchData().catch((err) => {
              console.error(err);
            });
          }}
          dataCy="refresh-button-ranks"
        />
      </TableHeader>
      <Table>
        <TableHead
          columns={columns}
          handleSort={handleSort}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
        />
        <TableBody columns={columns} data={displayData} />
      </Table>
    </TableContainer>
  );
}
